/* eslint-disable no-nested-ternary */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, ListGroup, ListGroupItem, Collapse } from 'reactstrap';
import { NavLink as ClassNavLink, useLocation } from 'react-router-dom';
import { startCase } from 'lodash';
import styled from 'styled-components';
import queryString from 'query-string';

// temporarily convert react-router-dom's class link to a functional navlink to work with
// reactstrap. (new version accounts for this. can remove after upgrade)
/* eslint-disable-next-line react/prop-types */
const NavLink = ({ hasActiveChild, isChild, ...p }) => <ClassNavLink {...p} />;

const SIcon = styled(FontAwesomeIcon)`
  transition: all .2s ease-in;
`;

const Chevron = ({ isOpen }) => (
  <SIcon icon="chevron-right" className={`ml-2 ${isOpen ? 'fa-rotate-90' : ''} float-right`} size="xs" />
);

Chevron.propTypes = {
  isOpen: PropTypes.bool,
};

Chevron.defaultProps = {
  isOpen: false,
};

const SideNavItem = ({ item, isChild }) => {
  const location = useLocation();
  const [color, setColor] = useState('dark');
  const [active, setActive] = useState(false);

  const hasIcon = item.icon !== undefined;
  const hasBadge = item.badge !== undefined;
  const hasChildren = item.items && item.items.length > 0;

  const hasActiveChild = hasChildren && item.items.some(i =>
    ((i.currentWhen?.map(cw => cw.split('.')[1]).filter(cw => location.pathname.includes(cw))) || []).length > 0 || (i.route && location.pathname.includes(i.route)));

  const [isOpen, setIsOpen] = useState(hasActiveChild || item.isOpen);
  const toggle = () => setIsOpen(!isOpen);

  const itemProps = {
    tag: hasChildren ? ListGroupItem : NavLink,
    onClick: hasChildren ? toggle : undefined,
    to: hasChildren ? undefined : item.pathname && !!item.queryParams ? {
      pathname: item.pathname,
      search: queryString.stringify(JSON.parse(item.queryParams)),
    } : item.pathname,
  };

  useEffect(() => {
    const a = ((item.currentWhen?.map(cw => cw.split('.')[1]).filter(cw => location.pathname.includes(cw))) || []).length > 0 || (item.route && location.pathname.includes(item.route));

    setColor(a ? 'light' : hasActiveChild ? 'dark' : 'tertiary');
    setActive(a);
  }, [location]);

  useEffect(() => {
    setIsOpen(hasActiveChild);
  }, [hasActiveChild]);

  return (
    <>
      <ListGroupItem
        action
        className={`side-nav-item d-flex align-items-center border-dark ${isChild ? 'is-child' : hasChildren ? 'border-bottom-0 has-children' : ''} ${hasActiveChild && 'has-active-child'} ${isOpen && 'is-open'}`}
        isChild={isChild}
        hasActiveChild={hasActiveChild}
        active={active}
        {...itemProps}
      >
        <span className={isChild ? 'ml-2' : ''}>
          {hasIcon && <FontAwesomeIcon fixedWidth icon={item.icon} className="mr-2" />}

          {item.label.replace(/\w+/g, startCase)}
        </span>

        {hasBadge &&
          <Badge color={color} pill className="ml-auto">
            {item.badge}
          </Badge>
        }

        {hasChildren && <Chevron isOpen={isOpen} />}
      </ListGroupItem>

      {hasChildren &&
        <Collapse isOpen={isOpen}>
          <ListGroup flush>
            {item.items.map(ii => (
              <SideNavItem key={`${ii.pathname}${ii.queryParams ? JSON.stringify(ii.queryParams) : ''}${Math.random()}`} item={ii} isChild />
            ))}
          </ListGroup>
        </Collapse>
      }
    </>
  );
};

SideNavItem.propTypes = {
  item: PropTypes.shape({
    route: PropTypes.string,
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      route: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })),
    badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  isChild: PropTypes.bool,
};

SideNavItem.defaultProps = {
  isChild: false,
};

export default SideNavItem;
export { Chevron };
